import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Icon } from "@qgiv/core-react";
import "./AccordionPanel.scss";

const AccordionPanel = ({
    activePanel,
    children,
    handlePanelClick,
    label,
    iconCollapsed = {
        glyph: "minus-circle-regular",
        type: "FontAwesome",
    },
    iconExpanded = {
        glyph: "plus-circle-regular",
        type: "FontAwesome",
    },
    renderLabelContent = ({ label }) => {
        return <>{label}</>;
    },
    size = "normal",
}) => {
    let isActive = label === activePanel;
    const renderLabelContentOptions = { label, isActive };
    const handleClick = () => {
        handlePanelClick(label);
    };
    // Since the Accordion Panel is no longer a button, we need to
    // manually trigger the keydown to open the accordion.
    const handleToggleKeyDown = (event) => {
        const isEnterOrSpacePressed =
            event.keyCode === 13 || event.keyCode === 32;
        // quick return for all other types of key press
        if (!isEnterOrSpacePressed) {
            return;
        }
        handlePanelClick(label);
    };
    const panelClasses = cx(
        "qg-vendor-accordion__panel",
        "panel",
        "accordion__panel",
        isActive && "accordion__panel--active",
    );
    const panelHeadingClasses = cx(
        "panel__heading",
        `panel__heading--${size}`,
        isActive && "panel__heading--active",
        "-cursor--pointer",
    );
    const panelContentClasses = cx(
        "panel__content",
        isActive && "panel__content--active",
    );
    const ariaLabel = `${label} Accordion`;
    const ariaExpanded = isActive ? "true" : "false";

    // The accordion header is a div with a role of button so that the user
    // knows that clicking the button element will change the appearance of the
    // page. However, it is not an actual button element as this is a generic
    // component and we need to be able to nest other HTML element such as a
    // button element inside of it. This is important in use cases such as the
    // receipt page where we have a print button nested inside of it.
    return (
        <div className={panelClasses} key={label}>
            <div
                role="button"
                tabIndex="0"
                aria-label={ariaLabel}
                aria-expanded={ariaExpanded}
                className={panelHeadingClasses}
                onClick={handleClick}
                onKeyDown={(e) => handleToggleKeyDown(e)}
            >
                {isActive ? (
                    <Icon
                        className="panel__heading__icon"
                        glyph={iconCollapsed.glyph}
                        type={iconCollapsed.type}
                    />
                ) : (
                    <Icon
                        className="panel__heading__icon"
                        glyph={iconExpanded.glyph}
                        type={iconExpanded.type}
                    />
                )}
                <div className="panel__heading__content">
                    {renderLabelContent(renderLabelContentOptions)}
                </div>
            </div>
            <div className={panelContentClasses}>{children}</div>
        </div>
    );
};

AccordionPanel.propTypes = {
    activePanel: PropTypes.string,
    iconCollapsed: PropTypes.shape({
        glyph: PropTypes.string,
        type: PropTypes.string,
    }),
    iconExpanded: PropTypes.shape({
        glyph: PropTypes.string,
        type: PropTypes.string,
    }),
    label: PropTypes.string.isRequired,
    handlePanelClick: PropTypes.func.isRequired,
    /**
     * @function renderLabelContent
     * @description Render function that allows overriding of the default
     *              label. Allows use of complex jsx templates to be used
     *              in place of the plain label.
     * @param {object} options Options object
     * @param {boolean} options.isActive Flag determining if the section is open
     * @param {string} options.label Label for the section
     */
    renderLabelContent: PropTypes.func,
    size: PropTypes.string,
    children: PropTypes.instanceOf(Object).isRequired,
};

export default AccordionPanel;
